import React from "react";
import { StyleSheet, View, TouchableOpacity, Image } from "react-native";
import { FontAwesome } from "@expo/vector-icons";
import { ThemedText } from "../ThemedText";
import { ThemedChip } from "../ThemedChip";
import { ThemedButton } from "../ThemedButton";
import { ThemedCompactCard } from "../cards/ThemedCompactCard";
import { ThemedMediumCard } from "../cards/ThemedMediumCard";
import { ThemedFullCard } from "../cards/ThemedFullCard";
import { ThemedCustomCard } from "../cards/ThemedCustomCard";
import { ThemedKebabMenu, type IKebabActionItem } from "../ThemedKebabMenu";
import { useThemeColor } from "@/hooks/useThemeColor";
import { ButtonPaddings } from "@/constants/themes/components";
import { BlueLinenRange, BrandColors } from "@/constants/ThemeColours";
import { AppUserModel, GroupModel, DEFAULT_STORAGE_QUOTA_BYTES, formatBytes } from "common247";
import { type ThemedListViewMode } from "../ThemedList";

export type UserWithGroup = AppUserModel & {
  allContactsGroup?: GroupModel;
};

/**
 * Props for the UserCard component implementing the 5 Card Tiers.
 */
export interface IUserCardProps {
  /** The user data model */
  user: UserWithGroup;
  /** Active view mode tier: "chip" | "compact" | "medium" | "full" | "custom" */
  viewMode?: ThemedListViewMode;
  /** Selection toggle / selection mode */
  selectionMode?: boolean;
  /** Whether the card is selected */
  isSelected?: boolean;
  /** Selection toggle callback */
  onSelect?: () => void;
  /** Callback to open user edit modal */
  onEdit?: (user: UserWithGroup) => void;
  /** Callback to open user edit modal directly on storage quota */
  onEditQuota?: (user: UserWithGroup) => void;
  /** Callback to toggle user active status */
  onToggleStatus?: (user: UserWithGroup, targetActive: boolean) => void;
  /** Callback to create and link All Contacts group */
  onCreateGroup?: (userId: string) => void;
  /** Optional kebab action overrides */
  kebabActions?: IKebabActionItem[];
  /** Optional custom container style */
  style?: any;
  /** Diagnostic test identifier */
  testID?: string;
}

/**
 * UserCard: Standardized 5-Tier Item Card Component for Registered Users.
 *
 * Tier 1 (Chip): Ultra-compact inline capsule displaying Avatar/Icon, Display Name, Account Status.
 * Tier 2 (Compact): Grid/selector card with avatar thumbnail, title, email, and actions.
 * Tier 3 (Medium): Balanced catalogue specimen with Header, Role/Status Badges, Quota, All Contacts Group.
 * Tier 4 (Full): Partitioned folio with Account Details, All Contacts Group, Storage Quota progress, Claims, Timestamps.
 * Tier 5 (Custom): User-configurable field & section layout adhering to ICustomCardConfig.
 */
export function UserCard({
  user,
  viewMode = "medium",
  selectionMode = false,
  isSelected = false,
  onSelect,
  onEdit,
  onEditQuota,
  onToggleStatus,
  onCreateGroup,
  kebabActions: customKebabActions,
  style,
  testID,
}: IUserCardProps): React.JSX.Element {
  const cardBg = useThemeColor({}, "card");
  const borderColor = useThemeColor({}, "border");
  const backgroundElement = useThemeColor({}, "backgroundElement");
  const textColor = useThemeColor({}, "text");
  const textMuted = useThemeColor({}, "textMuted");
  const inputReadOnlyBg = useThemeColor({}, "inputReadOnlyBg");
  const inputReadOnlyBorder = useThemeColor({}, "inputReadOnlyBorder");
  const inputReadOnlyTextColor = useThemeColor({}, "inputReadOnlyTextColor");

  const userId = user.userId || "user";
  const displayName = user.displayName || user.email || "(no name)";
  const isServiceAccount = user.userType === "serviceaccount";
  const isUserActive = user.active !== false && !user.disabled;
  const isGoogleAuth = user.authProvider === "google";
  const authProviderLabel = isGoogleAuth ? "Google IDP" : "User/Pass";
  const memberCount = user.allContactsGroup?.contactIds?.length ?? null;
  const createdLabel = user.createdAt ? new Date(user.createdAt).toLocaleDateString() : "Unknown";
  const lastLoggedOnLabel = user.lastSignInTime
    ? new Date(user.lastSignInTime).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "Never";

  const rawQuotaBytes = (user as any).storageQuota ?? DEFAULT_STORAGE_QUOTA_BYTES;
  const quotaBytes =
    typeof rawQuotaBytes === "number" && !isNaN(rawQuotaBytes)
      ? rawQuotaBytes
      : DEFAULT_STORAGE_QUOTA_BYTES;
  const quotaGb = ((quotaBytes || 0) / (1024 * 1024 * 1024)).toFixed(1);

  // Default kebab menu actions
  const defaultKebabActions: IKebabActionItem[] = [
    isUserActive
      ? {
          id: "deactivate",
          label: "Deactivate User",
          icon: "person-remove-outline",
          destructive: true,
          onPress: () => onToggleStatus?.(user, false),
        }
      : {
          id: "reactivate",
          label: "Reactivate User",
          icon: "person-add-outline",
          destructive: false,
          onPress: () => onToggleStatus?.(user, true),
        },
    {
      id: "edit",
      label: "Edit User",
      icon: "create-outline",
      onPress: () => onEdit?.(user),
    },
    {
      id: "edit-quota",
      label: "Edit Storage Quota",
      icon: "cloud-upload-outline",
      onPress: () => onEditQuota?.(user),
    },
  ];

  const effectiveKebabActions = customKebabActions || defaultKebabActions;

  const handleCardPress = () => {
    if (selectionMode && onSelect) {
      onSelect();
      return;
    }
    if (onEdit) {
      onEdit(user);
    }
  };

  /**
   * Helper to render custom claims badges
   */
  const renderClaimsBadges = (claims?: Record<string, any>): React.JSX.Element => {
    if (!claims || Object.keys(claims).length === 0) {
      return (
        <ThemedText style={[styles.noClaims, { color: textMuted }]}>
          No claims recorded
        </ThemedText>
      );
    }
    return (
      <View style={styles.claimsRow}>
        {Object.entries(claims).map(([key, value]) => (
          <View
            key={key}
            style={[
              styles.claimBadge,
              { backgroundColor: inputReadOnlyBg, borderColor: inputReadOnlyBorder },
            ]}
          >
            <ThemedText style={[styles.claimKey, { color: inputReadOnlyTextColor }]}>
              {key}:
            </ThemedText>
            <ThemedText style={[styles.claimValue, { color: textColor }]}>
              {Array.isArray(value) ? value.join(", ") : String(value)}
            </ThemedText>
          </View>
        ))}
      </View>
    );
  };

  // -------------------------------------------------------------
  // TIER 1: CHIP CARD
  // -------------------------------------------------------------
  if (viewMode === "chip") {
    return (
      <View style={[{ margin: 2 }, style]}>
        <ThemedChip
          entityType="user"
          id={userId}
          label={displayName}
          secondaryLabel={isServiceAccount ? "Service" : user.email}
          thumbnailUrl={user.photoIconUrl || user.photoUrl}
          isOwner={isUserActive}
          onPress={handleCardPress}
          testID={testID}
        />
      </View>
    );
  }

  // -------------------------------------------------------------
  // TIER 2: COMPACT CARD
  // -------------------------------------------------------------
  if (viewMode === "compact") {
    return (
      <View style={[{ position: "relative", width: "100%" }, style]}>
        <ThemedCompactCard
          entityType="user"
          id={userId}
          title={displayName}
          date={user.email}
          thumbnailUrl={user.photoIconUrl || user.photoUrl}
          isOwner={isUserActive}
          selectionMode={selectionMode}
          isSelected={isSelected}
          onSelect={onSelect}
          onPress={handleCardPress}
          kebabActions={effectiveKebabActions}
          testID={testID}
        />
      </View>
    );
  }

  // -------------------------------------------------------------
  // TIER 5: CUSTOM CARD
  // -------------------------------------------------------------
  if (viewMode === "custom") {
    return (
      <View style={[{ width: "100%" }, style]}>
        <ThemedCustomCard
          entityType="user"
          id={userId}
          item={{
            id: userId,
            displayName,
            title: displayName,
            email: user.email,
            userType: isServiceAccount ? "serviceaccount" : "user",
            authProvider: user.authProvider,
            storageQuota: quotaBytes,
            thumbnailUrl: user.photoIconUrl || user.photoUrl,
            claims: user.claims,
            active: isUserActive,
          }}
          title={displayName}
          email={user.email}
          thumbnailUrl={user.photoIconUrl || user.photoUrl}
          isOwner={isUserActive}
          kebabActions={effectiveKebabActions}
          onPress={handleCardPress}
          testID={testID}
        />
      </View>
    );
  }



  // -------------------------------------------------------------
  // TIER 3 (MEDIUM) & TIER 4 (FULL) SPECIMEN CARDS
  // -------------------------------------------------------------
  const isFullCard = viewMode === "full";

  return (
    <TouchableOpacity
      testID={testID}
      activeOpacity={0.7}
      onPress={handleCardPress}
      style={[
        styles.card,
        { backgroundColor: cardBg, borderColor },
        isFullCard && styles.fullCardPadding,
        style,
      ]}
    >
      {/* 1. Card Header */}
      <View style={styles.cardHeader}>
        <View style={styles.titleArea}>
          <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
            {user.photoIconUrl || user.photoUrl ? (
              <Image
                source={{ uri: user.photoIconUrl || user.photoUrl }}
                style={styles.avatarImage}
              />
            ) : null}
            <View style={{ flex: 1 }}>
              <ThemedText type="defaultSemiBold" style={styles.cardTitle} numberOfLines={1}>
                {displayName}
              </ThemedText>
              <ThemedText style={[styles.emailText, { color: textMuted }]} numberOfLines={1}>
                {user.email}
              </ThemedText>
            </View>
          </View>
          <ThemedText type="code" style={[styles.idText, { color: textMuted }]} numberOfLines={1}>
            {userId}
          </ThemedText>
        </View>

        <View style={styles.cardHeaderActions}>
          <View
            style={[
              styles.statusBadge,
              isUserActive
                ? {
                    backgroundColor: "rgba(16, 185, 129, 0.12)",
                    borderColor: "rgba(16, 185, 129, 0.35)",
                  }
                : {
                    backgroundColor: "rgba(239, 68, 68, 0.12)",
                    borderColor: "rgba(239, 68, 68, 0.35)",
                  },
            ]}
          >
            <ThemedText
              type="stamp"
              style={[
                styles.statusBadgeText,
                { color: isUserActive ? "#059669" : "#DC2626" },
              ]}
            >
              {isUserActive ? "active" : "deactivated"}
            </ThemedText>
          </View>

          <View
            style={[
              styles.typeBadge,
              isServiceAccount
                ? {
                    backgroundColor: "rgba(255, 91, 3, 0.12)",
                    borderColor: "rgba(255, 91, 3, 0.35)",
                  }
                : {
                    backgroundColor: BlueLinenRange.wash,
                    borderColor: BlueLinenRange.border,
                  },
            ]}
          >
            <ThemedText
              type="stamp"
              style={[
                styles.typeBadgeText,
                {
                  color: isServiceAccount
                    ? BrandColors.sungoldOrange.hex
                    : BlueLinenRange.archival,
                },
              ]}
            >
              {isServiceAccount ? "service account" : "user"}
            </ThemedText>
          </View>

          <View
            style={[
              styles.authBadge,
              isGoogleAuth
                ? {
                    backgroundColor: "rgba(234, 67, 53, 0.12)",
                    borderColor: "rgba(234, 67, 53, 0.35)",
                  }
                : {
                    backgroundColor: "rgba(37, 99, 235, 0.12)",
                    borderColor: "rgba(37, 99, 235, 0.35)",
                  },
            ]}
          >
            <FontAwesome
              name={isGoogleAuth ? "google" : "key"}
              size={10}
              color={isGoogleAuth ? "#EA4335" : "#2563EB"}
              style={{ marginRight: 4 }}
            />
            <ThemedText
              type="stamp"
              style={[
                styles.authBadgeText,
                {
                  color: isGoogleAuth ? "#EA4335" : "#2563EB",
                },
              ]}
            >
              {authProviderLabel}
            </ThemedText>
          </View>

          {user.betaGroup ? (
            <View
              style={[
                styles.authBadge,
                {
                  backgroundColor: "rgba(5, 150, 105, 0.12)",
                  borderColor: "rgba(5, 150, 105, 0.35)",
                },
              ]}
              testID="user-card-beta-badge"
            >
              <FontAwesome name="flask" size={10} color="#059669" style={{ marginRight: 4 }} />
              <ThemedText
                type="stamp"
                style={[styles.authBadgeText, { color: "#059669", fontWeight: "700" }]}
              >
                {`beta: ${user.betaGroup}`}
              </ThemedText>
            </View>
          ) : null}

          <ThemedKebabMenu
            actions={effectiveKebabActions}
            accessibilityLabel={`Actions for ${displayName}`}
          />
        </View>
      </View>

      {/* 2. All Contacts Group */}
      <View
        style={[
          styles.groupRow,
          { backgroundColor: backgroundElement, borderColor },
        ]}
      >
        <View style={styles.groupInfo}>
          <ThemedText type="stamp" style={[styles.sectionLabel, { color: textMuted }]}>
            ALL CONTACTS GROUP
          </ThemedText>
          {user.allContactsGroupId ? (
            <View style={styles.groupDetails}>
              <ThemedText type="code" style={[styles.groupId, { color: textMuted }]} numberOfLines={1}>
                {user.allContactsGroupId}
              </ThemedText>
              <View
                style={[
                  styles.memberCountBadge,
                  {
                    backgroundColor: "rgba(71, 81, 11, 0.12)",
                    borderColor: "rgba(71, 81, 11, 0.3)",
                  },
                ]}
              >
                <ThemedText
                  style={[styles.memberCountText, { color: BrandColors.grassyGreen.hex }]}
                >
                  {memberCount !== null
                    ? `${memberCount} member${memberCount !== 1 ? "s" : ""}`
                    : "loading…"}
                </ThemedText>
              </View>
            </View>
          ) : (
            <View style={styles.createGroupRow}>
              <ThemedText style={[styles.missingText, { color: BrandColors.beetRed.hex }]}>
                No group linked
              </ThemedText>
              {onCreateGroup && (
                <ThemedButton
                  title="Create Group"
                  variant="secondary"
                  onPress={(e) => {
                    e.stopPropagation();
                    onCreateGroup(userId);
                  }}
                  style={styles.createGroupBtn}
                />
              )}
            </View>
          )}
        </View>
      </View>

      {/* 3. Storage Quota */}
      <View
        style={[
          styles.groupRow,
          { backgroundColor: backgroundElement, borderColor },
        ]}
      >
        <View style={styles.groupInfo}>
          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <ThemedText type="stamp" style={[styles.sectionLabel, { color: textMuted }]}>
              STORAGE QUOTA
            </ThemedText>
            {onEditQuota && (
              <ThemedButton
                title="Edit Quota"
                variant="minimal"
                onPress={(e) => {
                  e.stopPropagation();
                  onEditQuota(user);
                }}
                style={{ paddingVertical: 2, paddingHorizontal: 6 }}
              />
            )}
          </View>
          <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
            <View
              style={[
                styles.memberCountBadge,
                {
                  backgroundColor: "rgba(59, 130, 246, 0.12)",
                  borderColor: "rgba(59, 130, 246, 0.3)",
                },
              ]}
            >
              <ThemedText
                style={[styles.memberCountText, { color: "#2563EB", fontWeight: "600" }]}
              >
                {formatBytes(quotaBytes)} ({quotaGb} GB)
              </ThemedText>
            </View>
          </View>
        </View>
      </View>

      {/* 4. Claims (Rendered for Full Card, or if Medium and claims exist) */}
      {(isFullCard || (user.claims && Object.keys(user.claims).length > 0)) && (
        <View style={styles.claimsSection}>
          <ThemedText type="stamp" style={[styles.sectionLabel, { color: textMuted }]}>
            CLAIMS
          </ThemedText>
          {renderClaimsBadges(user.claims as Record<string, any>)}
        </View>
      )}

      {/* 5. Footer Timestamps */}
      <View style={[styles.cardFooter, { borderTopColor: borderColor }]}>
        <ThemedText type="stamp" style={[styles.dateText, { color: textMuted }]}>
          RECORDED: {createdLabel}
        </ThemedText>
        <ThemedText type="stamp" style={[styles.dateText, { color: textMuted }]}>
          LAST LOGGED ON: {lastLoggedOnLabel}
        </ThemedText>
      </View>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  card: {
    borderWidth: 1,
    borderRadius: 0,
    padding: 12,
    marginBottom: 8,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 3,
    elevation: 2,
    width: "100%",
  },
  fullCardPadding: {
    padding: 16,
    gap: 4,
  },
  cardHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 10,
    gap: 8,
  },
  avatarImage: {
    width: 32,
    height: 32,
    borderRadius: 4,
  },
  titleArea: {
    flex: 1,
  },
  cardTitle: {
    fontSize: 14,
    lineHeight: 18,
  },
  emailText: {
    fontSize: 11,
    marginTop: 1,
  },
  idText: {
    fontSize: 9,
    marginTop: 2,
  },
  cardHeaderActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    flexWrap: "wrap",
    justifyContent: "flex-end",
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 4,
    borderWidth: 1,
    alignSelf: "flex-start",
  },
  statusBadgeSmall: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    borderWidth: 1,
    alignSelf: "center",
  },
  statusBadgeText: {
    fontSize: 10,
    fontWeight: "700",
  },
  statusBadgeTextSmall: {
    fontSize: 9,
    fontWeight: "700",
  },
  typeBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 4,
    borderWidth: 1,
    alignSelf: "flex-start",
  },
  typeBadgeText: {
    fontSize: 10,
    fontWeight: "700",
  },
  authBadge: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 4,
    borderWidth: 1,
    alignSelf: "flex-start",
  },
  authBadgeText: {
    fontSize: 10,
    fontWeight: "700",
  },
  groupRow: {
    borderWidth: 1,
    borderRadius: 2,
    padding: 8,
    marginBottom: 8,
  },
  groupInfo: {
    gap: 4,
  },
  sectionLabel: {
    fontSize: 9,
    fontWeight: "700",
    letterSpacing: 0.8,
  },
  groupDetails: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
  },
  groupId: {
    fontSize: 10,
    flex: 1,
  },
  memberCountBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 3,
    borderWidth: 1,
  },
  memberCountText: {
    fontSize: 10,
    fontWeight: "600",
  },
  createGroupRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: 2,
  },
  missingText: {
    fontSize: 11,
    fontWeight: "600",
  },
  createGroupBtn: {
    paddingVertical: ButtonPaddings.compact.paddingVertical,
    paddingHorizontal: ButtonPaddings.compact.paddingHorizontal,
  },
  claimsSection: {
    marginBottom: 8,
    gap: 4,
  },
  claimsRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 6,
  },
  claimBadge: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 3,
    borderWidth: 1,
    gap: 4,
  },
  claimKey: {
    fontSize: 10,
    fontWeight: "600",
  },
  claimValue: {
    fontSize: 10,
    fontWeight: "600",
  },
  noClaims: {
    fontSize: 10,
    fontStyle: "italic",
  },
  cardFooter: {
    borderTopWidth: 1,
    paddingTop: 8,
    marginTop: 4,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 4,
  },
  dateText: {
    fontSize: 9,
    letterSpacing: 0.5,
  },
});

export default UserCard;
